import React, { useState, useEffect } from 'react';
import {
  X,
  ShieldCheck,
  AlertTriangle,
  Info,
  CheckCircle2,
  Sparkles,
  Smartphone,
  Tablet,
  Monitor,
  RotateCw,
  Zap,
} from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { runSiteResponsiveAudit, autoFixSectionResponsive, scanDomForHorizontalOverflow } from '../../utils/responsiveQA';
import { ResponsiveQAIssue } from '../../types';
import confetti from 'canvas-confetti';

interface ResponsiveQAModalProps {
  onClose: () => void;
}

export const ResponsiveQAModal: React.FC<ResponsiveQAModalProps> = ({ onClose }) => {
  const { activeSite, updateSectionInActivePage, setDeviceMode } = useSite();
  const [issues, setIssues] = useState<ResponsiveQAIssue[]>([]);
  const [domScanResult, setDomScanResult] = useState<{
    hasOverflow: boolean;
    overflowingElements: any[];
  }>({ hasOverflow: false, overflowingElements: [] });
  const [isAuditing, setIsAuditing] = useState(false);
  const [fixedCount, setFixedCount] = useState(0);

  const runAudit = () => {
    if (!activeSite) return;
    setIsAuditing(true);
    setTimeout(() => {
      const siteIssues = runSiteResponsiveAudit(activeSite);
      setIssues(siteIssues);

      // Also scan real DOM in editor canvas
      const canvasContainer = document.querySelector('.editor-canvas-container') as HTMLElement;
      const domResult = scanDomForHorizontalOverflow(canvasContainer || document.body);
      setDomScanResult(domResult);

      setIsAuditing(false);
    }, 250);
  };

  useEffect(() => {
    runAudit();
  }, [activeSite]);

  if (!activeSite) return null;

  const handleFixIssue = (issue: ResponsiveQAIssue) => {
    if (!issue.sectionId || !activeSite.pages) return;

    for (const page of activeSite.pages) {
      const sec = (page?.sections || []).find((s) => s.id === issue.sectionId);
      if (sec) {
        const fixed = autoFixSectionResponsive(sec, issue.fixActionKey);
        updateSectionInActivePage(sec.id, fixed.content, fixed.style);
        setFixedCount((c) => c + 1);
        break;
      }
    }

    setIssues((prev) => prev.filter((i) => i.id !== issue.id));
  };

  const handleFixAll = () => {
    let count = 0;
    (activeSite.pages || []).forEach((page) => {
      (page?.sections || []).forEach((section) => {
        const fixed = autoFixSectionResponsive(section);
        updateSectionInActivePage(section.id, fixed.content, fixed.style);
        count++;
      });
    });

    setFixedCount(count);
    setIssues([]);

    confetti({
      particleCount: 80,
      spread: 70,
      origin: { y: 0.6 },
    });
  };

  const criticalIssues = issues.filter((i) => i.severity === 'critical');
  const warningIssues = issues.filter((i) => i.severity === 'warning');
  const infoIssues = issues.filter((i) => i.severity === 'info');

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 select-none">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="p-5 bg-slate-950 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-white">Responsive QA & Taşma Denetçisi</h3>
              <p className="text-xs text-slate-400">Mobil, Tablet ve Masaüstü uyumluluk analiz raporu</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={runAudit}
              disabled={isAuditing}
              title="Yeniden Denetle"
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
            >
              <RotateCw className={`w-4 h-4 ${isAuditing ? 'animate-spin text-blue-400' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-slate-800 hover:bg-rose-500/20 hover:text-rose-300 text-slate-400 transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Status Score Bar */}
        <div className="px-6 py-4 bg-slate-900/60 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
              <span className="text-xs font-bold text-slate-300">{criticalIssues.length} Kritik</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <span className="text-xs font-bold text-slate-300">{warningIssues.length} Uyarı</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
              <span className="text-xs font-bold text-slate-300">{infoIssues.length} Öneri</span>
            </div>
          </div>

          {issues.length > 0 && (
            <button
              onClick={handleFixAll}
              className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-lg transition flex items-center gap-1.5"
            >
              <Zap className="w-3.5 h-3.5" />
              <span>Tümünü Otomatik Düzelt</span>
            </button>
          )}
        </div>

        {/* Body Issues List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3 custom-scrollbar">
          {issues.length === 0 ? (
            <div className="py-12 flex flex-col items-center justify-center text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <div>
                <h4 className="text-base font-extrabold text-white">Harika! Hiçbir Responsive Hata Bulunamadı</h4>
                <p className="text-xs text-slate-400 max-w-sm mt-1">
                  Tüm sayfalar ve bölümler mobil (iPhone/Android), tablet ve masaüstü standartlarına kusursuz uyum sağlıyor.
                </p>
              </div>
            </div>
          ) : (
            issues.map((issue) => {
              const isCritical = issue.severity === 'critical';
              const isWarning = issue.severity === 'warning';

              return (
                <div
                  key={issue.id}
                  className={`p-4 rounded-2xl border transition flex items-start justify-between gap-4 ${
                    isCritical
                      ? 'bg-rose-500/10 border-rose-500/30 text-rose-200'
                      : isWarning
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                      : 'bg-blue-500/10 border-blue-500/30 text-blue-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {isCritical ? (
                        <AlertTriangle className="w-5 h-5 text-rose-400" />
                      ) : isWarning ? (
                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                      ) : (
                        <Info className="w-5 h-5 text-blue-400" />
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold">{issue.title}</span>
                        {issue.sectionTitle && (
                          <span className="text-[10px] px-2 py-0.5 rounded-md bg-slate-900/80 border border-slate-700 text-slate-300 font-mono">
                            {issue.sectionTitle}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-300/80 leading-relaxed">{issue.description}</p>
                    </div>
                  </div>

                  {issue.autoFixAvailable && (
                    <button
                      onClick={() => handleFixIssue(issue)}
                      className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 text-xs font-bold shrink-0 transition flex items-center gap-1"
                    >
                      <Sparkles className="w-3 h-3 text-blue-400" />
                      <span>Düzelt</span>
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-950 border-t border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Smartphone className="w-3.5 h-3.5 text-blue-400" />
            <span>Mobil Test Genişliği: 390px / Breakpoint: 768px</span>
          </div>

          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs transition"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};
